import API_BASE_URL from "config/api";

export const READINESS_STAGES = [
  "resolve_access",
  "install_loader",
  "configure_banner",
  "scan_catalog",
  "scan_in_progress",
  "review_catalog",
  "catalog_changes",
  "ready",
];

export const WAF_PROVIDERS = [
  { value: "cloudflare", label: "Cloudflare" },
  { value: "akamai", label: "Akamai" },
  { value: "imperva", label: "Imperva" },
  { value: "aws_waf", label: "AWS WAF" },
  { value: "sucuri", label: "Sucuri" },
  { value: "datadome", label: "DataDome" },
  { value: "other", label: "Diğer" },
];

const EMPTY_READINESS = {
  stage: "resolve_access",
  access: {
    state: "unreachable",
    reachable: false,
    wafProvider: null,
    needsProviderSelection: false,
    crawlerVerified: false,
  },
  integration: {
    embed: { present: false, mode: "none" },
    bannerLanguages: [],
    scanPrerequisitesMet: false,
    canShowGtm: false,
  },
  catalog: {
    definitionCount: 0,
    flaggedCount: 0,
    observedCookieCount: 0,
    hasCompletedScan: false,
    hasSuccessfulScan: false,
    isInitialScan: false,
    changes: {
      available: false,
      addedCount: 0,
      removedCount: 0,
      added: [],
      removed: [],
    },
  },
  scans: { active: null, latest: null, lastCompleted: null },
};

export function normalizeReadiness(payload) {
  const source = payload && typeof payload === "object" ? payload : {};
  const stage = source.stage === undefined
    ? EMPTY_READINESS.stage
    : READINESS_STAGES.includes(source.stage)
    ? source.stage
    : "unknown";
  return {
    ...EMPTY_READINESS,
    ...source,
    stage,
    access: { ...EMPTY_READINESS.access, ...(source.access || {}) },
    integration: {
      ...EMPTY_READINESS.integration,
      ...(source.integration || {}),
      embed: {
        ...EMPTY_READINESS.integration.embed,
        ...(source.integration?.embed || {}),
      },
      bannerLanguages: Array.isArray(source.integration?.bannerLanguages)
        ? source.integration.bannerLanguages
        : [],
    },
    catalog: {
      ...EMPTY_READINESS.catalog,
      ...(source.catalog || {}),
      changes: {
        ...EMPTY_READINESS.catalog.changes,
        ...(source.catalog?.changes || {}),
        added: Array.isArray(source.catalog?.changes?.added)
          ? source.catalog.changes.added
          : [],
        removed: Array.isArray(source.catalog?.changes?.removed)
          ? source.catalog.changes.removed
          : [],
      },
    },
    scans: { ...EMPTY_READINESS.scans, ...(source.scans || {}) },
  };
}

export function buildReadinessSteps(readiness) {
  const state = normalizeReadiness(readiness);
  const hasBanner = state.integration.bannerLanguages.length > 0;
  const hasCatalog = state.catalog.hasCompletedScan || state.catalog.hasSuccessfulScan;
  const initialReviewComplete = hasCatalog && (
    !state.catalog.isInitialScan || state.catalog.flaggedCount === 0
  );

  return [
    {
      key: "loader",
      label: "Kod kurulumu",
      complete: state.integration.embed.present,
      active: ["resolve_access", "install_loader"].includes(state.stage),
    },
    {
      key: "banner",
      label: "Banner ayarları",
      complete: hasBanner,
      active: state.stage === "configure_banner",
    },
    {
      key: "catalog",
      label: "Çerez taraması",
      complete: hasCatalog,
      active: ["scan_catalog", "scan_in_progress"].includes(state.stage),
    },
    {
      key: "review",
      label: "Katalog kontrolü",
      complete: initialReviewComplete,
      active: ["review_catalog", "catalog_changes"].includes(state.stage),
    },
    {
      key: "google",
      label: "Google Consent",
      complete: false,
      active: state.stage === "ready",
    },
  ];
}

export function getReadinessBadge(readiness) {
  const state = normalizeReadiness(readiness);
  const labels = {
    resolve_access: ["Erişim engeli", "error"],
    configure_banner: ["Banner ayarı gerekli", "warning"],
    scan_catalog: ["Tarama gerekli", "warning"],
    scan_in_progress: ["Tarama sürüyor", "info"],
    review_catalog: ["Katalog incelemesi gerekli", "warning"],
    catalog_changes: ["Tarama değişiklikleri hazır", "info"],
    install_loader: ["Kurulum kodu bekleniyor", "info"],
    ready: ["Entegrasyon tamamlandı", "success"],
  };
  return labels[state.stage] || ["Kontrol ediliyor", "warning"];
}

export function buildLoaderScript() {
  return '<script src="https://cdn.logicookie.com/logicookie.js" async defer></script>';
}

export function buildConsentModeScript(site, defaultPreferencesAllowed = false) {
  const tenantId = Number(site?.tenantId);
  const siteId = Number(site?.siteId);
  if (
    !Number.isSafeInteger(tenantId)
    || tenantId <= 0
    || !Number.isSafeInteger(siteId)
    || siteId <= 0
  ) return "";

  return `<script>
  window.LogiCookieInstance = new LogiCookie({
    tenantId: "${tenantId}",
    siteId: "${siteId}",
    enableConsentMode: true,
    defaultPreferences: "${defaultPreferencesAllowed ? "allowed" : "disallowed"}"
  });
</script>`;
}

export async function fetchIntegrationReadiness(siteId, token, signal) {
  const response = await fetch(
    `${API_BASE_URL}/api/integration/readiness?siteId=${encodeURIComponent(siteId)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    }
  );
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || "Entegrasyon durumu alınamadı");
    error.status = response.status;
    throw error;
  }
  const readiness = normalizeReadiness(payload);
  if (String(readiness.site?.id || "") !== String(siteId)) {
    throw new Error("Entegrasyon yanıtı seçili siteyle eşleşmiyor");
  }
  return readiness;
}
